/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App, Segmented } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  API_ACCESS_SUCCESS_MESSAGES,
  PLUGIN_VIEW_ID_FIELD,
  PLUGIN_VIEW_ID_LABEL,
  buildEmbedSnippet,
  buildPluginCfg,
  scriptOnlyDisplayOptions,
  type EmbedSnippetVariant,
  type PluginEmbedSettings,
  type PluginKey,
} from '@sportninja/common/features/account/apiAccess'
import Button from 'src/components/ts-components/Button'
import EmptyState from 'src/components/ts-components/EmptyState'
import Text from 'src/components/ts-components/Text'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import { segmentedToggleStyle } from 'src/styles/segmentedToggleStyle'
import {
  API_ACCESS_EMPTY_MIN_HEIGHT,
  PLUGIN_KEYS_FAILED_MESSAGE,
  WEBSITE_PLUGIN_CARDS,
} from '../constants'
import { ApiAccessCard } from './ApiAccessCard'

interface EmbedCodeSectionProps {
  /** The selected key; `null` when the account has none. */
  pluginKey: PluginKey | null
  /** The keys failed to load: say so instead of asking for a key. */
  keysFailed: boolean
}

const VARIANT_OPTIONS: { label: string; value: EmbedSnippetVariant }[] = [
  { label: 'Script', value: 'script' },
  { label: 'No-Script Builders', value: 'noScript' },
]

/**
 * The snippet to paste into the league's website: the selected key plus
 * every setting changed from the plugin's default. Invalid settings are left
 * out until fixed.
 */
export const EmbedCodeSection = ({
  pluginKey,
  keysFailed,
}: EmbedCodeSectionProps) => {
  const { message } = App.useApp()
  const { values } = useFormikContext<PluginEmbedSettings>()
  const [variant, setVariant] = useState<EmbedSnippetVariant>('script')

  const renderBody = () => {
    if (!pluginKey && keysFailed) {
      return (
        <EmptyState
          title={capitalizeWords(PLUGIN_KEYS_FAILED_MESSAGE.title)}
          minHeight={API_ACCESS_EMPTY_MIN_HEIGHT}
          subtitle={PLUGIN_KEYS_FAILED_MESSAGE.subtitle}
        />
      )
    }
    if (!pluginKey) {
      return (
        <EmptyState
          title={capitalizeWords('Create A Key First')}
          minHeight={API_ACCESS_EMPTY_MIN_HEIGHT}
          subtitle='Your embed code appears here once you create a Website Plugin key above.'
        />
      )
    }

    const cfg = buildPluginCfg(values)
    const missingId = !cfg[PLUGIN_VIEW_ID_FIELD[cfg.view]]
    // The No-Script reader has no attribute for these; name what gets dropped.
    const scriptOnly =
      variant === 'noScript'
        ? scriptOnlyDisplayOptions(cfg).map((label) => capitalizeWords(label))
        : []
    const snippet = buildEmbedSnippet(
      { pluginKey: pluginKey.key, cfg },
      variant
    )
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(snippet)
        message.success(
          formatSuccessToast(API_ACCESS_SUCCESS_MESSAGES.embedCodeCopied)
        )
      } catch {
        message.error(
          'Could not copy the embed code. Select it and copy instead.'
        )
      }
    }

    return (
      <>
        <div css={controlsStyle}>
          <Segmented
            css={segmentedToggleStyle}
            aria-label='Embed code type'
            value={variant}
            onChange={(value) => setVariant(value as EmbedSnippetVariant)}
            options={VARIANT_OPTIONS}
          />
        </div>
        <pre css={snippetStyle}>{snippet}</pre>
        {missingId ? (
          <Text variant='paragraphXSmall' color={colors.WARNING_300}>
            {`Enter a valid ${
              PLUGIN_VIEW_ID_LABEL[cfg.view]
            } in the Embed settings — the code doesn't work without one.`}
          </Text>
        ) : null}
        {scriptOnly.length ? (
          <Text variant='paragraphXSmall' color={colors.WARNING_300}>
            {`${scriptOnly.join(', ')} ${
              scriptOnly.length > 1 ? 'need' : 'needs'
            } the Script code — the No-Script code can't carry ${
              scriptOnly.length > 1 ? 'them' : 'it'
            }.`}
          </Text>
        ) : null}
        <div css={footerStyle}>
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
            {variant === 'script'
              ? 'Paste it where the plugin should appear on your website.'
              : 'For site builders that remove scripts from custom HTML.'}
          </Text>
          <Button
            size='compact'
            variant='primary'
            icon={<CopyOutlined />}
            text={capitalizeWords('Copy Embed Code')}
            onClick={copy}
          />
        </div>
      </>
    )
  }

  return (
    <ApiAccessCard
      title={capitalizeWords(WEBSITE_PLUGIN_CARDS.embed.title)}
      subtitle={WEBSITE_PLUGIN_CARDS.embed.subtitle}
    >
      {renderBody()}
    </ApiAccessCard>
  )
}

const controlsStyle = css`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`

const snippetStyle = css`
  margin: 0;
  padding: 12px;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid ${colors.SOFT_STEEL};
  background-color: ${colors.SECONDARY_300};
  color: ${colors.WHITE};
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre;
`

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`
